import React, { useState, useRef, useEffect } from 'react';
import { FaBook, FaClock, FaTags, FaImage, FaNewspaper, FaChevronDown } from 'react-icons/fa';
import { NavLink, useLocation, Link } from 'react-router-dom';

import { Logo } from '../common';
import { HamburgerMenu } from '../ui';

const Header: React.FC = () => {
  const [showDictionarySubmenu, setShowDictionarySubmenu] = useState(false);
  const dictionaryMenuRef = useRef<HTMLLIElement>(null);
  const location = useLocation();

  // 共通のCSSクラス定数
  const MAIN_MENU_HOVER_CLASSES = 'hover:bg-amber-900 hover:bg-opacity-30';
  const SUBMENU_ITEM_CLASS = 'flex items-center px-4 py-2 text-sm';
  const SUBMENU_HOVER_CLASS = 'hover:bg-gray-100';
  const SUBMENU_INACTIVE_COLOR = 'text-gray-700';

  // 緑色のカラーコード
  const activeColor = 'text-green-600'; // 緑色

  // クリック外部検出のためのイベントリスナー
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dictionaryMenuRef.current && !dictionaryMenuRef.current.contains(event.target as Node)) {
        setShowDictionarySubmenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dictionaryMenuRef]);

  const toggleDictionarySubmenu = () => {
    setShowDictionarySubmenu(!showDictionarySubmenu);
  };

  return (
    <header className="red-earth-bg px-1 border-b border-amber-800">
      {/* ロゴとナビゲーションを横並びに配置 */}
      <div className="flex justify-between items-center h-16">
        {/* ロゴ部分 */}
        <Link to="/" className="flex items-center px-2 py-1 text-amber-100 h-16">
          <div className="w-10 h-14 mr-1 flex items-center justify-center">
            <Logo />
          </div>
          <div className="flex flex-col justify-center items-center h-full">
            <span className="text-xs font-light tracking-wider">古代生物図鑑</span>
            <span
              className="text-3xl tracking-wide leading-none"
              style={{
                fontFamily: "'Rubik Burned', system-ui",
                color: '#f3e8d0', // 明るいクリーム色で視認性向上
                textShadow: '2px 2px 4px rgba(0,0,0,0.5), 1px 1px 2px rgba(139,90,60,0.8)', // 影を追加してコントラスト向上
                letterSpacing: '0.02em',
                display: 'inline-block',
                transform: 'rotate(-2deg)', // 少し傾けて古代感を出す
              }}
            >
              Siska
            </span>
          </div>
        </Link>

        {/* ナビゲーション部分 */}
        <div className="flex items-center space-x-4">
          <nav className="hidden md:block">
            <ul className="flex flex-wrap">
              {/* 図鑑メニュー（サブメニュー付き） */}
              <li ref={dictionaryMenuRef} className="relative">
                <button
                  onClick={toggleDictionarySubmenu}
                  className={`flex items-center px-4 py-2 whitespace-nowrap ${MAIN_MENU_HOVER_CLASSES} focus:outline-none ${
                    ['/creatures', '/era', '/gallery'].some(path =>
                      location.pathname.startsWith(path)
                    )
                      ? activeColor
                      : 'text-amber-100'
                  }`}
                >
                  <FaBook className="mr-2" />
                  図鑑
                  <FaChevronDown
                    className={`ml-1 transition-transform ${showDictionarySubmenu ? 'rotate-180' : ''}`}
                    size={12}
                  />
                </button>

                {/* サブメニュー */}
                {showDictionarySubmenu && (
                  <div className="absolute left-0 mt-1 w-48 bg-white rounded-lg shadow-lg z-10 py-2 border border-gray-200">
                    <NavLink
                      to="/creatures"
                      className={({ isActive }) =>
                        `${SUBMENU_ITEM_CLASS} ${isActive ? activeColor : SUBMENU_INACTIVE_COLOR} ${SUBMENU_HOVER_CLASS} transition-colors`
                      }
                      onClick={() => setShowDictionarySubmenu(false)}
                    >
                      <FaBook className="mr-3" size={16} />
                      全生物一覧
                    </NavLink>
                    <NavLink
                      to="/era"
                      className={({ isActive }) =>
                        `${SUBMENU_ITEM_CLASS} ${isActive ? activeColor : SUBMENU_INACTIVE_COLOR} ${SUBMENU_HOVER_CLASS} transition-colors`
                      }
                      onClick={() => setShowDictionarySubmenu(false)}
                    >
                      <FaClock className="mr-3" size={16} />
                      時代別
                    </NavLink>
                    <NavLink
                      to="/creatures/classifications"
                      className={({ isActive }) =>
                        `${SUBMENU_ITEM_CLASS} ${isActive ? activeColor : SUBMENU_INACTIVE_COLOR} ${SUBMENU_HOVER_CLASS} transition-colors`
                      }
                      onClick={() => setShowDictionarySubmenu(false)}
                    >
                      <FaTags className="mr-3" size={16} />
                      分類別
                    </NavLink>
                    <NavLink
                      to="/gallery"
                      className={({ isActive }) =>
                        `${SUBMENU_ITEM_CLASS} ${isActive ? activeColor : SUBMENU_INACTIVE_COLOR} ${SUBMENU_HOVER_CLASS} transition-colors`
                      }
                      onClick={() => setShowDictionarySubmenu(false)}
                    >
                      <FaImage className="mr-3" size={16} />
                      ギャラリー
                    </NavLink>
                  </div>
                )}
              </li>

              {/* 他のメニュー項目（サブメニューに移動したものは削除） */}
              <li>
                <NavLink
                  to="/news"
                  className={({ isActive }) =>
                    `flex items-center px-4 py-2 ${isActive ? activeColor : 'text-amber-100'} whitespace-nowrap ${MAIN_MENU_HOVER_CLASSES}`
                  }
                >
                  <FaNewspaper className="mr-2" />
                  ニュース
                </NavLink>
              </li>
            </ul>
          </nav>

          {/* ハンバーガーメニュー */}
          <HamburgerMenu />
        </div>
      </div>
    </header>
  );
};

export default Header;
